/**
 * La barra de controles del calendario: disposición, nueva cita, filtros y tests.
 */
import { useState } from 'react'

import { cn } from '../lib/cn'
import { ServiciosParaCrear } from './calendario-crear'
import { ChipFiltro, FilaFiltro } from './calendario-filtros'

export type Vista = 'dia' | 'semana' | 'mes'

export function BarraCalendario({
    disposicion,
    elegirDisposicion,
    variasColumnas,
    propuestas,
    guardando,
    alGuardar,
    alDescartar,
    incluirTests,
    setIncluirTests,
    mostrarIncluirTests = true,
    mostrarNuevaCita,
    servicios,
    servicioParaCrear,
    alElegirServicio,
    hintCrear,
    serviciosOcultos,
    alAlternarServ,
    alVerTodosServ,
    alActualizar,
    actualizando,
    mostrarOcultarCerrados = false,
    ocultarCerrados = false,
    alAlternarCerrados,
    incluirPasadas = false,
    setIncluirPasadas,
}: {
    disposicion: 'columnas' | 'juntos'
    elegirDisposicion: (v: 'columnas' | 'juntos') => void
    variasColumnas: boolean
    propuestas: number
    guardando: boolean
    alGuardar: () => void
    alDescartar: () => void
    incluirTests: boolean
    setIncluirTests: (v: boolean) => void
    mostrarIncluirTests?: boolean
    mostrarNuevaCita: boolean
    servicios: { id: number; nombre: string; duracionMin: number | null }[]
    servicioParaCrear: number | null
    alElegirServicio: (id: number | null) => void
    hintCrear?: string
    serviciosOcultos: Set<number>
    alAlternarServ: (id: number) => void
    alVerTodosServ: () => void
    alActualizar: () => void
    actualizando: boolean
    mostrarOcultarCerrados?: boolean | undefined
    ocultarCerrados?: boolean | undefined
    alAlternarCerrados?: (() => void) | undefined
    incluirPasadas?: boolean | undefined
    setIncluirPasadas?: ((v: boolean) => void) | undefined
}) {
    const [creando, setCreando] = useState(false)
    const [filtrosAbiertos, setFiltrosAbiertos] = useState(false)
    const abierto = creando || servicioParaCrear !== null
    const hayFiltros = servicios.length > 0
    const filtrosOcultos = serviciosOcultos.size

    function toggleNuevaCita() {
        if (abierto) {
            setCreando(false)
            alElegirServicio(null)
            return
        }
        setCreando(true)
    }

    return (
        <div className="space-y-3 rounded-lg border border-neutral-800 bg-neutral-900/40 p-3">
            <div className="flex flex-wrap items-center gap-2">
                {variasColumnas && (
                    <div className="flex items-center rounded-md border border-neutral-800">
                        {(
                            [
                                ['columnas', 'Por profesional'],
                                ['juntos', 'Todos juntos'],
                            ] as const
                        ).map(([v, et]) => (
                            <button
                                key={v}
                                onClick={() => elegirDisposicion(v)}
                                aria-pressed={disposicion === v}
                                className={
                                    'min-h-11 px-2.5 py-1 text-xs transition md:min-h-0 ' +
                                    (disposicion === v
                                        ? 'bg-neutral-800 text-neutral-100'
                                        : 'text-neutral-500 hover:text-neutral-300')
                                }
                            >
                                {et}
                            </button>
                        ))}
                    </div>
                )}

                {mostrarNuevaCita && (
                    <button
                        type="button"
                        data-testid="nueva-cita"
                        onClick={toggleNuevaCita}
                        aria-pressed={abierto}
                        className={
                            'min-h-11 rounded-md border px-3 py-1 text-xs transition md:min-h-0 ' +
                            (abierto
                                ? 'border-sky-500/60 bg-sky-500/15 text-sky-200'
                                : 'border-sky-500/40 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20')
                        }
                    >
                        + Nueva cita
                    </button>
                )}

                <button
                    type="button"
                    data-testid="actualizar-calendario"
                    onClick={alActualizar}
                    disabled={actualizando}
                    className="min-h-11 rounded-md border border-neutral-800 px-3 py-1 text-xs text-neutral-300 hover:text-neutral-100 disabled:opacity-50 md:min-h-0"
                >
                    {actualizando ? 'Actualizando…' : 'Actualizar'}
                </button>

                {mostrarOcultarCerrados && alAlternarCerrados && (
                    <button
                        type="button"
                        data-testid="ocultar-cerrados"
                        onClick={alAlternarCerrados}
                        aria-pressed={ocultarCerrados}
                        className={
                            'min-h-11 rounded-md border px-3 py-1 text-xs transition md:min-h-0 ' +
                            (ocultarCerrados
                                ? 'border-neutral-500 bg-neutral-800 text-neutral-100'
                                : 'border-neutral-800 text-neutral-400 hover:text-neutral-200')
                        }
                    >
                        {ocultarCerrados ? 'Mostrar cerrados' : 'Ocultar cerrados'}
                    </button>
                )}

                {propuestas > 0 && (
                    <div className="ml-auto flex items-center gap-2">
                        <button
                            onClick={alDescartar}
                            className="min-h-11 rounded-md border border-neutral-800 px-3 py-1 text-xs text-neutral-400 hover:text-neutral-100 md:min-h-0"
                        >
                            Descartar
                        </button>
                        <button
                            onClick={alGuardar}
                            disabled={guardando}
                            className="min-h-11 rounded-md bg-emerald-500/90 px-3 py-1 text-xs font-medium text-neutral-950 hover:bg-emerald-400 disabled:opacity-50 md:min-h-0"
                        >
                            {guardando
                                ? 'Guardando…'
                                : `Guardar ${propuestas} cambio${propuestas > 1 ? 's' : ''}`}
                        </button>
                    </div>
                )}

                {hayFiltros && (
                    <button
                        type="button"
                        data-testid="filtros-calendario"
                        aria-expanded={filtrosAbiertos}
                        aria-controls="filtros-calendario"
                        onClick={() => setFiltrosAbiertos((v) => !v)}
                        className={
                            'min-h-11 rounded-md border px-3 py-1 text-xs transition md:hidden ' +
                            (filtrosAbiertos || filtrosOcultos > 0
                                ? 'border-sky-500/50 bg-sky-500/10 text-sky-200'
                                : 'border-neutral-800 text-neutral-300 hover:text-neutral-100')
                        }
                    >
                        Filtros{filtrosOcultos > 0 ? ` · ${filtrosOcultos}` : ''}
                    </button>
                )}

                {setIncluirPasadas && (
                    <button
                        type="button"
                        data-testid="incluir-pasadas"
                        onClick={() => setIncluirPasadas(!incluirPasadas)}
                        aria-pressed={incluirPasadas}
                        title="Suma también las citas de esta vista que ya han empezado"
                        className={
                            'min-h-11 rounded-md border px-3 py-1 text-xs transition md:min-h-0 ' +
                            (incluirPasadas
                                ? 'border-sky-500/60 bg-sky-500/15 text-sky-200'
                                : 'border-neutral-800 text-neutral-500 hover:text-neutral-300')
                        }
                    >
                        Incluir reservas pasadas
                    </button>
                )}

                {mostrarIncluirTests && (
                    <button
                        onClick={() => setIncluirTests(!incluirTests)}
                        aria-pressed={incluirTests}
                        className={
                            'min-h-11 rounded-md border px-3 py-1 text-xs transition md:min-h-0 ' +
                            (propuestas > 0 ? '' : 'ml-auto ') +
                            (incluirTests
                                ? 'border-amber-500/60 bg-amber-500/15 text-amber-300'
                                : 'border-neutral-800 text-neutral-500 hover:text-neutral-300')
                        }
                    >
                        🧪 {incluirTests ? 'Tests INCLUIDOS' : 'Incluir tests'}
                    </button>
                )}
            </div>

            {abierto && (
                <ServiciosParaCrear
                    servicios={servicios}
                    elegido={servicioParaCrear}
                    alElegir={alElegirServicio}
                    {...(hintCrear !== undefined ? { hint: hintCrear } : {})}
                />
            )}

            {hayFiltros && (
                <div
                    id="filtros-calendario"
                    className={cn('space-y-2', !filtrosAbiertos && 'hidden md:block')}
                >
                    {servicios.length > 0 && (
                        <FilaFiltro
                            rotulo="Servicios"
                            hayOcultos={serviciosOcultos.size > 0}
                            alVerTodos={alVerTodosServ}
                        >
                            {servicios.map((s) => (
                                <ChipFiltro
                                    key={s.id}
                                    activo={!serviciosOcultos.has(s.id)}
                                    onClick={() => alAlternarServ(s.id)}
                                >
                                    {s.nombre || `Servicio ${s.id}`}
                                    {s.duracionMin !== null && (
                                        <span className="ml-1 text-neutral-500">{s.duracionMin}′</span>
                                    )}
                                </ChipFiltro>
                            ))}
                        </FilaFiltro>
                    )}
                </div>
            )}
        </div>
    )
}
